import React from 'react'
import {
  PROGI_ULGI, WZROST_CEN_DOMYSLNY, LATA_ANALIZY, ROK_WYMIANY_FALOWNIKA, EMISJA_KG_CO2_KWH,
} from '../../lib/oplacalnosc'
import { opisCen } from '../../lib/teczka/zalozenia'
import { zapiszDaneProjektu } from '../../lib/daneProjektu'
import BudgetSlider from '../kits/BudgetSlider'

/**
 * Kiedy instalacja się zwróci.
 *
 * TRZY ZASADY, które trzymają tę sekcję po stronie rachunku, a nie obietnicy:
 *  1. wynik podajemy dla WIDEŁEK kosztu — bo kwota instalacji też jest widełkami,
 *  2. scenariusz bez wzrostu cen energii stoi zawsze obok domyślnego,
 *  3. każde założenie jest widoczne i można je zmienić w tym samym oknie.
 *
 * Zwrot bez tych trzech rzeczy byłby jedną ładną liczbą, której nikt nie umiałby
 * sprawdzić — a taka liczba w dokumencie z logo i datą jest gorsza niż jej brak.
 */

const zl = (v) => `${Math.round(v).toLocaleString('pl-PL')} zł`

/**
 * Odmiana „rok" przez liczbę. Polski ma trzy formy, a przy liczbie ułamkowej
 * wymaga dopełniacza liczby pojedynczej („3,9 roku"), nie mnogiej.
 */
const odmienLata = (v) => {
  if (!Number.isInteger(v)) return 'roku'
  if (v === 1) return 'rok'
  const dziesiatki = v % 100
  const jednosci = v % 10
  if (jednosci >= 2 && jednosci <= 4 && (dziesiatki < 12 || dziesiatki > 14)) return 'lata'
  return 'lat'
}

const liczbaPl = (v) => String(v).replace('.', ',')
const lataTekst = (v) => (v == null ? 'nie zwraca się' : `${liczbaPl(v)} ${odmienLata(v)}`)

/** Zakres lat — odmiana idzie za GÓRNĄ granicą, bo to ona stoi przy rzeczowniku. */
const zakresLat = (od, doo) => {
  if (od == null || doo == null) return lataTekst(od ?? doo)
  if (od === doo) return lataTekst(od)
  return `${liczbaPl(od)}–${liczbaPl(doo)} ${odmienLata(doo)}`
}

/** Wykres skumulowanego przepływu — moment przecięcia zera to cała odpowiedź. */
const WykresZwrotu = ({ lata, zwrotLata }) => {
  const W = 620
  const H = 150
  const M = { gora: 12, dol: 20, lewo: 46, prawo: 10 }
  const wartosci = lata.map((l) => l.skumulowanyZl)
  const min = Math.min(...wartosci, 0)
  const max = Math.max(...wartosci, 0)
  const rozpietosc = max - min || 1

  const x = (rok) => M.lewo + ((rok - 1) / (LATA_ANALIZY - 1)) * (W - M.lewo - M.prawo)
  const y = (v) => H - M.dol - ((v - min) / rozpietosc) * (H - M.gora - M.dol)

  const linia = lata.map((l, i) => `${i === 0 ? 'M' : 'L'} ${x(l.rok)} ${y(l.skumulowanyZl)}`).join(' ')
  const obszar = `${linia} L ${x(LATA_ANALIZY)} ${y(0)} L ${x(1)} ${y(0)} Z`

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img"
         aria-label="Skumulowany bilans inwestycji w kolejnych latach">
      <defs>
        <linearGradient id="grad-zwrot" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#10b981" stopOpacity="0.28" />
          <stop offset="100%" stopColor="#10b981" stopOpacity="0.02" />
        </linearGradient>
      </defs>

      <path d={obszar} fill="url(#grad-zwrot)" />
      <line x1={M.lewo} y1={y(0)} x2={W - M.prawo} y2={y(0)} stroke="rgb(var(--vv-bd-gray-300))" strokeWidth="1" />
      <text x={M.lewo - 6} y={y(0) + 3} textAnchor="end" fontSize="8.5" fill="rgb(var(--vv-tx-gray-400))">0 zł</text>
      <text x={M.lewo - 6} y={y(min) + 3} textAnchor="end" fontSize="8.5" fill="rgb(var(--vv-tx-gray-400))">{zl(min)}</text>
      <text x={M.lewo - 6} y={y(max) + 3} textAnchor="end" fontSize="8.5" fill="rgb(var(--vv-tx-gray-400))">{zl(max)}</text>

      <path d={linia} fill="none" stroke="#059669" strokeWidth="2" strokeLinejoin="round" />

      {zwrotLata != null && (
        <g>
          <line x1={x(zwrotLata)} y1={M.gora} x2={x(zwrotLata)} y2={H - M.dol}
                stroke="#059669" strokeWidth="1.5" strokeDasharray="4 3" />
          <text x={x(zwrotLata)} y={M.gora + 8} textAnchor="middle" fontSize="9.5"
                fontWeight="700" fill="rgb(var(--vv-tx-emerald-700))">{lataTekst(zwrotLata)}</text>
        </g>
      )}

      {/* Rok wymiany falownika — widoczne załamanie krzywej musi mieć podpis,
          inaczej wygląda jak błąd obliczeń. */}
      <text x={x(ROK_WYMIANY_FALOWNIKA)} y={H - 6} textAnchor="middle" fontSize="8" fill="rgb(var(--vv-tx-gray-400))">
        ↑ wymiana falownika
      </text>
      {[1, 5, 10, 15, 20, 25].map((rok) => (
        <text key={rok} x={x(rok)} y={H - 6} textAnchor="middle" fontSize="8.5" fill="rgb(var(--vv-tx-gray-400))">
          {rok === ROK_WYMIANY_FALOWNIKA ? '' : rok}
        </text>
      ))}
    </svg>
  )
}

const Pole = ({ etykieta, wartosc, onZmiana, przyrostek = 'zł', podpowiedz }) => (
  <label className="block">
    <span className="block text-xs font-medium text-gray-600 mb-1">{etykieta}</span>
    <div className="relative">
      <input type="number" min="0" step="500" inputMode="numeric"
             value={wartosc || ''} placeholder={podpowiedz}
             onChange={(e) => onZmiana(Number(e.target.value) || 0)}
             className="w-full pl-3 pr-9 py-2 rounded-lg border border-gray-200 text-sm tabular-nums
                        focus:outline-none focus:ring-2 focus:ring-blue-500" />
      <span className="absolute right-3 top-2 text-xs text-gray-400">{przyrostek}</span>
    </div>
  </label>
)

/**
 * Props:
 *   dane, onZmiana  odpowiedzi VoltValue Projektu (ulga, dotacja, wzrost cen), zapisywane w sesji
 *   onEdycja        (opcjonalnie) przy kliencie: dostaje samą zmianę (`{ ulga }`) zamiast zapisu w sesji
 *   ekonomia        wynik wspólnego silnika `ekonomiaKlienta` (`lib/ekonomiaKlienta.js`), policzony raz
 *                   w oknie dokumentu: ta sama prognoza idzie na okładkę, stronę produkcji i tutaj
 */
const SekcjaOplacalnosci = ({ dane, onZmiana, ekonomia, onEdycja = null }) => {
  // Przy kliencie edycja wraca do jego karty (`onEdycja`, `lib/klienci/energia.js`), nie do sessionStorage:
  // odpowiedzi w sessionStorage przechodziły z klienta na klienta.
  const ustaw = (czesc) => (onEdycja ? onEdycja(czesc) : onZmiana(zapiszDaneProjektu(czesc)))

  // Koszt rozstrzyga silnik: cena klienta, potem cena oferty albo suma zestawu, na końcu typowe
  // widełki w zł/kWp (`podpowiedzKosztu`). Jedna kwota daje widełki o zerowej szerokości.
  const kosztOd = ekonomia?.koszt?.odZl ?? 0
  const kosztDo = ekonomia?.koszt?.doZl ?? 0
  const wzrostCen = dane.wzrostCen ?? WZROST_CEN_DOMYSLNY
  const pusty = { tani: null, drogi: null, taniBezWzrostu: null, drogiBezWzrostu: null }
  const wynik = ekonomia?.zakres || pusty
  const magazyn = ekonomia?.porownanieZMagazynem || null
  // W tytule porównania pojemność nominalna z zestawu; model liczy z użytkowej, gdy karta ją podaje.
  const pojemnoscTytulu = magazyn?.maMagazynWZestawie && ekonomia?.magazyn
    ? Math.round(ekonomia.magazyn.nominalnaKwh * 10) / 10
    : magazyn?.pojemnoscKwh

  const brakZuzycia = !wynik.tani

  return (
    <section className="space-y-4">
      <h3 className="text-sm font-bold text-gray-900">Kiedy się zwróci</h3>

      {/* Pól „Koszt instalacji od/do" tu już NIE MA. Kwota jest liczona
          z faktycznego zestawu i ustawień konta (stawki montażu, marża, VAT),
          więc wpisywanie jej z ręki dawało drugą, konkurencyjną liczbę obok
          ceny końcowej z tego samego dokumentu. Widełki zwrotu liczymy nadal
          z zakresu, ale wyprowadzonego z tej jednej kwoty. */}

      <div className="grid sm:grid-cols-3 gap-3 print:hidden">
        <label className="block">
          <span className="block text-xs font-medium text-gray-600 mb-1">
            Ulga termomodernizacyjna
          </span>
          <select value={dane.ulga} onChange={(e) => ustaw({ ulga: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white
                             focus:outline-none focus:ring-2 focus:ring-blue-500">
            {PROGI_ULGI.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
          </select>
        </label>
        <Pole etykieta="Dotacja" wartosc={dane.dotacjaZl}
              onZmiana={(v) => ustaw({ dotacjaZl: v })} podpowiedz="0" />
        <label className="block">
          <span className="block text-xs font-medium text-gray-600 mb-1">
            Wzrost cen energii: <span className="font-bold text-gray-900 tabular-nums">
              {Math.round(wzrostCen * 100)}%
            </span> rocznie
          </span>
          {/* Ten sam suwak, co przy budżecie i cenie w filtrach — jeden element
              sterujący w dwóch wersjach graficznych to dwa razy więcej do utrzymania. */}
          <div className="mt-2.5">
            <BudgetSlider
              value={Math.round(wzrostCen * 100)} min={0} max={6} step={1}
              onChange={(v) => ustaw({ wzrostCen: Number(v) / 100 })}
              ariaLabel="Zakładany roczny wzrost cen energii"
            />
          </div>
        </label>
      </div>

      {brakZuzycia ? (
        <p className="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5">
          Podaj roczne zużycie prądu w sekcji produkcji. Bez niego nie da się rozdzielić
          energii zużytej u siebie od oddanej do sieci, a różnią się one ceną ponad trzykrotnie.
        </p>
      ) : (
        <>
          {/* Kafelek OD LEWEJ, na szerokość treści — pas przez całą stronę
              robił z tej liczby nagłówek sekcji zamiast jednej z informacji. */}
          <div className="inline-block rounded-xl border border-emerald-200 bg-emerald-50/60 px-4 py-3">
            <p className="text-xs text-emerald-800 mb-0.5">Zwrot inwestycji</p>
            <p className="text-2xl font-bold text-emerald-900 tabular-nums leading-tight">
              {zakresLat(wynik.tani.zwrotLata, wynik.drogi.zwrotLata)}
            </p>
            <p className="text-xs text-emerald-700 mt-1">
              {/* Gdy koszt jest POLICZONY, a nie wpisany widełkami, obie granice
                  są równe — „23 615 zł–23 615 zł" wyglądałoby na usterkę. */}
              przy koszcie {kosztOd === kosztDo ? zl(kosztOd) : `${zl(kosztOd)}–${zl(kosztDo)}`}
              {' '}i wzroście cen energii o {Math.round(wzrostCen * 100)}% rocznie
            </p>
            <p className="text-xs text-emerald-700/80 mt-1.5">
              Gdyby ceny energii nie rosły wcale:{' '}
              {zakresLat(wynik.taniBezWzrostu.zwrotLata, wynik.drogiBezWzrostu.zwrotLata)}
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
            <div className="rounded-lg border border-gray-200 px-3 py-2">
              <p className="text-gray-500">Oszczędność w 1. roku</p>
              <p className="text-sm font-bold text-gray-900 tabular-nums">
                {zl(wynik.tani.lata[0].przychodZl)}
              </p>
            </div>
            <div className="rounded-lg border border-gray-200 px-3 py-2">
              <p className="text-gray-500">Ulga podatkowa</p>
              <p className="text-sm font-bold text-gray-900 tabular-nums">
                {zl(wynik.tani.ulgaZl)}
              </p>
            </div>
            <div className="rounded-lg border border-gray-200 px-3 py-2">
              <p className="text-gray-500">Bilans po {LATA_ANALIZY} latach</p>
              <p className="text-sm font-bold text-gray-900 tabular-nums">
                {zl(wynik.drogi.zyskPo25LatachZl)}–{zl(wynik.tani.zyskPo25LatachZl)}
              </p>
            </div>
            <div className="rounded-lg border border-gray-200 px-3 py-2">
              <p className="text-gray-500">Unikniona emisja</p>
              <p className="text-sm font-bold text-gray-900 tabular-nums">
                {Math.round(wynik.tani.lata.reduce((s, l) => s + l.produkcjaKwh, 0)
                  * EMISJA_KG_CO2_KWH / 1000)} t CO₂
              </p>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-3">
            <WykresZwrotu lata={wynik.tani.lata} zwrotLata={wynik.tani.zwrotLata} />
          </div>

          {/* Z magazynem i bez — dla środka widełek kosztu */}
          {magazyn && (
            <div>
              <p className="text-xs font-semibold text-gray-500 mb-2">
                Z magazynem i bez
              </p>
              <div className="grid sm:grid-cols-2 gap-2">
                {[
                  { ...magazyn.bezMagazynu, tytul: 'Bez magazynu', opis: 'nadwyżki idą do sieci' },
                  {
                    ...magazyn.zMagazynem,
                    tytul: `Z magazynem ${liczbaPl(pojemnoscTytulu)} kWh`,
                    opis: magazyn.maMagazynWZestawie
                      ? 'magazyn z Twojego zestawu'
                      : `dopłata ok. ${zl(magazyn.kosztMagazynuZl)}`,
                  },
                ].map((w) => (
                  <div key={w.tytul} className="rounded-lg border border-gray-200 px-3 py-2.5">
                    <p className="text-xs font-semibold text-gray-700">{w.tytul}</p>
                    <p className="text-[11px] text-gray-400 mb-1.5">{w.opis}</p>
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="text-[11px] text-gray-500">zwrot</span>
                      <span className="text-sm font-bold text-gray-900 tabular-nums">
                        {lataTekst(w.zwrotLata)}
                      </span>
                    </div>
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="text-[11px] text-gray-500">bilans po {LATA_ANALIZY} latach</span>
                      <span className="text-sm font-bold text-gray-900 tabular-nums">
                        {zl(w.zyskPo25LatachZl)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-gray-400 mt-1.5">
                Magazyn podnosi autokonsumpcję, a energia zużyta u siebie jest warta ponad
                trzy razy tyle, co sprzedana, ale kosztuje. Który wariant wygrywa, zależy
                od Twojego profilu zużycia i ceny baterii.
              </p>
            </div>
          )}

          <p className="text-[11px] text-gray-400 leading-relaxed">
            Założenia: {opisCen(ekonomia?.rozliczenie)}, degradacja modułów 0,5% rocznie, wymiana falownika
            w {ROK_WYMIANY_FALOWNIKA}. roku ({zl(wynik.tani.wymianaFalownikaZl)}).
            To scenariusz przy dzisiejszych cenach, a nie prognoza rynku: ceny energii
            i stawki rynkowe zmieniają się niezależnie od instalacji.
          </p>
        </>
      )}
    </section>
  )
}

export default SekcjaOplacalnosci
